import { useMemo, useState } from 'react';

import { useAiUsageMetrics } from '../hooks/useAiUsageMetrics';
import type { AiUsageMetricsFilter } from '../api';

export interface PromptVersionInfo {
  versionId: string;
  number: number;
  isActive: boolean;
}

interface PromptVersionComparisonProps {
  versions: PromptVersionInfo[];
}

interface KpiBlockProps {
  label: string;
  versionId: string | null;
  versions: PromptVersionInfo[];
  onChange: (versionId: string | null) => void;
}

function formatCurrency(amount: number, currency: string): string {
  try {
    return amount.toLocaleString('es-ES', { style: 'currency', currency });
  } catch {
    return `${amount.toLocaleString('es-ES')} ${currency}`;
  }
}

function formatNumber(value: number): string {
  return value.toLocaleString('es-ES');
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function KpiBlock({ label, versionId, versions, onChange }: KpiBlockProps): JSX.Element {
  const to = useMemo(() => new Date(), []);
  const from = useMemo(() => {
    const d = new Date(to);
    d.setUTCDate(d.getUTCDate() - 30);
    return d;
  }, [to]);

  const filter: AiUsageMetricsFilter = useMemo(
    () => ({
      from: from.toISOString(),
      to: to.toISOString(),
      groupBy: 'Day',
      topCount: 1,
      ...(versionId ? { promptTemplateVersionId: versionId } : {}),
    }),
    [from, to, versionId],
  );

  const metrics = useAiUsageMetrics(filter);
  const summary = versionId ? metrics.data?.summary ?? null : null;
  const currency = summary?.currency ?? 'USD';

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</span>
        <select
          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs"
          value={versionId ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        >
          <option value="">— Selecciona —</option>
          {versions.map((v) => (
            <option key={v.versionId} value={v.versionId}>
              v{v.number}{v.isActive ? ' (activa)' : ''}
            </option>
          ))}
        </select>
      </div>

      <dl className="mt-4 space-y-2 text-sm">
        <div className="flex items-baseline justify-between">
          <dt className="text-slate-500">Llamadas</dt>
          <dd className="font-semibold tabular-nums text-slate-900">{summary ? formatNumber(summary.callCount) : '—'}</dd>
        </div>
        <div className="flex items-baseline justify-between">
          <dt className="text-slate-500">Tokens (in + out)</dt>
          <dd className="font-semibold tabular-nums text-slate-900">
            {summary ? formatNumber(summary.inputTokensTotal + summary.outputTokensTotal) : '—'}
          </dd>
        </div>
        <div className="flex items-baseline justify-between">
          <dt className="text-slate-500">Coste 30d</dt>
          <dd className="font-semibold tabular-nums text-slate-900">
            {summary ? formatCurrency(summary.costTotal, currency) : '—'}
          </dd>
        </div>
        <div className="flex items-baseline justify-between">
          <dt className="text-slate-500">Tasa de éxito</dt>
          <dd className="font-semibold tabular-nums text-slate-900">
            {summary ? `${(summary.successRate * 100).toFixed(1)}%` : '—'}
          </dd>
        </div>
        <div className="flex items-baseline justify-between">
          <dt className="text-slate-500">Latencia media</dt>
          <dd className="font-semibold tabular-nums text-slate-900">
            {summary ? formatMs(summary.averageLatencyMs) : '—'}
          </dd>
        </div>
        {summary && summary.callCount > 0 ? (
          <div className="flex items-baseline justify-between text-xs">
            <dt className="text-slate-400">Coste por llamada</dt>
            <dd className="tabular-nums text-slate-500">
              {formatCurrency(summary.costTotal / summary.callCount, currency)}
            </dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}

/**
 * Renders two KPI panels side-by-side, each scoped to a prompt-template version. Lets the admin
 * compare cost / latency / success rate between any two versions of the same prompt.
 *
 * The data source is the same <c>GET /admin/metrics/ai-usage</c> endpoint with the
 * <c>promptTemplateVersionId</c> filter — Cosmos doesn't carry a special "version comparison"
 * query, so the page simply issues two filtered queries and lets React Query cache them
 * independently.
 */
export function PromptVersionComparison({ versions }: PromptVersionComparisonProps): JSX.Element {
  const active = useMemo(() => versions.find((v) => v.isActive) ?? versions[0] ?? null, [versions]);
  const previous = useMemo(() => {
    const sorted = [...versions].sort((a, b) => b.number - a.number);
    const first = sorted.find((v) => v.versionId !== active?.versionId) ?? null;
    return first;
  }, [versions, active]);

  const [leftId, setLeftId] = useState<string | null>(active?.versionId ?? null);
  const [rightId, setRightId] = useState<string | null>(previous?.versionId ?? null);

  if (versions.length < 2) {
    return (
      <div className="card border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
        Necesitas al menos dos versiones para comparar consumo.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold text-slate-900">Comparativa de versiones — últimos 30 días</h2>
      <p className="text-xs text-slate-500">
        Compara coste y latencia entre dos versiones del prompt. Cada panel filtra por
        <code className="mx-1 rounded bg-slate-100 px-1 font-mono text-[11px]">promptTemplateVersionId</code>.
      </p>
      <div className="grid gap-4 lg:grid-cols-2">
        <KpiBlock label="Versión A" versionId={leftId} versions={versions} onChange={setLeftId} />
        <KpiBlock label="Versión B" versionId={rightId} versions={versions} onChange={setRightId} />
      </div>
    </div>
  );
}
